import * as keyBy from 'lodash/keyBy'
import * as omit from 'lodash/omit'
import * as uniq from 'lodash/uniq'
import * as isEqual from 'lodash/isEqual'
import * as intersection from 'lodash/intersection'
import { Collection } from 'interfaces'
import { CursorPaginatable } from 'interfaces/table'


export function normalize<T extends {}, K extends keyof T> (items: T[], key: K): Collection<T, K> {
  const allKeys = uniq(items.map((item) => item[key]))
  const byKey = keyBy(items, key) as Collection<T, K>['byKey']

  return { byKey, allKeys }
}

type SetItemInCollectionOptions = {
  mergeIfExist: boolean
  checkEquality: boolean
}
const DEFAULT_SET_ITEM_OPTIONS: SetItemInCollectionOptions = {
  mergeIfExist: false,
  checkEquality: false,
}
export function setItemInCollection<T extends {}, K extends keyof T> (collection: Collection<T, K> | null, item: Partial<T> & Pick<T, K>, keyProp: K, options: { mergeIfExist: true } & Partial<SetItemInCollectionOptions>): Collection<T, K>
export function setItemInCollection<T extends {}, K extends keyof T> (collection: Collection<T, K> | null, item: T, keyProp: K, options?: Partial<SetItemInCollectionOptions>): Collection<T, K>
export function setItemInCollection<T extends {}, K extends keyof T> (collection: Collection<T, K> | null, item: T, keyProp: K, options: Partial<SetItemInCollectionOptions> = DEFAULT_SET_ITEM_OPTIONS): Collection<T, K> {
  let key = (item as any)[keyProp]
  if (key === undefined) {
    key = keyProp
  }

  const { mergeIfExist, checkEquality } = Object.assign({}, DEFAULT_SET_ITEM_OPTIONS, options)

  const nextCollection = (collection || createEmptyCollection<T, K>()) as Collection<T, K>
  const nextItem = getItemFromCollection(nextCollection, key)
  if (nextItem === item || checkEquality && isEqual(nextItem, item)) {
    return nextCollection
  }

  if (mergeIfExist) {
    item = { ...(nextItem as Object), ...(item as Object) } as T
  }

  return {
    byKey: Object.assign({}, nextCollection.byKey, { [key]: item }),
    allKeys: uniq(nextCollection.allKeys.concat(key)),
  }
}

export function removeByKeyFromCollection<T extends {}, K extends keyof T> (collection: Collection<T, K> | null, ...removed: T[K][]): Collection<T, K> {
  if (!collection) {
    return createEmptyCollection()
  }

  if (!intersection(collection.allKeys, removed)) {
    return collection
  }

  const removedSet = new Set(removed)
  return {
    byKey: omit(collection.byKey, removed) as Collection<T, K>['byKey'],
    allKeys: collection.allKeys.filter((key) => !removedSet.has(key)),
  }
}

export function concatCollections<T extends {}, K extends keyof T> (first: Collection<T, K> | null, second: Collection<T, K>): Collection<T, K> {
  if (!first) {
    return second
  }

  if (!second.allKeys.length) {
    return first
  }

  return {
    byKey: Object.assign({}, first.byKey, second.byKey),
    allKeys: uniq(first.allKeys.concat(second.allKeys)),
  }
}

export const getItemFromCollection = <T extends {}, K extends keyof T>(collection: Collection<T, K> | null, key: T[K] | null): T | null =>
  key && collection ? (collection.byKey[key] || null) : null


export function getItemsByKey<T> (byKey: { [key: string]: T }, keys: (string | number)[]): T[] {
  return keys.reduce((result: T[], key) => {
    const item = byKey[key]
    return item ? result.concat(item) : result
  }, [])
}

export function getItemsFromCollection<T, K extends keyof T> (collection: Collection<T, K>): T[] {
  return getItemsByKey<T>(collection.byKey, collection.allKeys as any)
}

/*
 * `any` here is intentionally: we don't care about actual types inside a `Collection`.
 * We do care only about { allKeys: any[] } property
 */
export function isCollectionEmpty (collection: Collection<any, any>): boolean {
  return !collection || !collection.allKeys.length
}

export function collectionLength (collection: Collection<any, any> | null): number | null {
  return collection ? collection.allKeys.length : null
}

export const EMPTY_COLLECTION: Collection<any, any> = {
  byKey: {},
  allKeys: [] as any[],
}

export const createEmptyCollection = <T extends {}, K extends keyof T>(): Collection<T, K> => ({
  byKey: {} as Collection<T, K>['byKey'],
  allKeys: [] as T[K][],
})

export const concatPaginatables = <T>(prev: CursorPaginatable<T>, next: CursorPaginatable<T> | null): CursorPaginatable<T> => {
  if (!next) {
    return prev
  }

  return {
    ...prev,
    ...next,
    results: prev.results.concat(next.results),
  }
}

export function filterCollection <T, K extends keyof T> (
  filterFunc: (item: T, key: T[K]) => boolean,
  collection: Collection<T, K>,
): Collection<T, K> {
  const nextKeys = collection.allKeys.filter((key) => {
    return filterFunc(collection.byKey[key], key)
  })

  return {
    allKeys: nextKeys,
    byKey: nextKeys.reduce<Collection<T, K>['byKey']>((acc, key) => {
      acc[key] = collection.byKey[key]
      return acc
    }, {} as Collection<T, K>['byKey']),
  }
}

